import { api, type ApiResponse } from '@/lib/api';
import type {
  CreateSlaPolicyRequest,
  ListResponse,
  ReorderSlaPoliciesRequest,
  SlaPolicyQuery,
  SlaPolicyResponse,
  UpdateSlaPolicyRequest,
} from '@troia-v3/types';

const BASE_URL = '/sla-policies';

/**
 * Cliente HTTP do módulo de Políticas de SLA.
 *
 * Espelha as rotas do Plano B (`TROIA-V3-BACKEND/src/modules/sla-policies/router.ts`):
 * - GET    /api/sla-policies             — lista paginada
 * - GET    /api/sla-policies/:id         — uma política
 * - POST   /api/sla-policies             — cria
 * - PUT    /api/sla-policies/:id         — atualiza (parcial)
 * - DELETE /api/sla-policies/:id         — remove
 * - POST   /api/sla-policies/reorder     — grava `order` a partir da posição no array
 * - POST   /api/sla-policies/:id/set-default — promove a padrão
 *
 * `isDefault` NÃO trafega em create/update: o Zod do servidor é `.strict()` e devolve 422.
 * A promoção passa obrigatoriamente por `setDefault` — é o que mantém a invariante
 * "exatamente uma política padrão por empresa" em um único caminho.
 *
 * Ficam de fora desta camada (decisão do pré-flight, não omissão): `POST /activate` e
 * `GET /activation` — a estreia do motor de SLA é operação de deploy, não de admin de
 * tenant, e não tem lugar nesta tela.
 */
export const slaPoliciesApi = {
  async list(query?: SlaPolicyQuery): Promise<ListResponse<SlaPolicyResponse>> {
    const response = await api.get<ApiResponse<ListResponse<SlaPolicyResponse>>>(BASE_URL, { params: query });
    return response.data.data!;
  },

  async getById(id: string): Promise<SlaPolicyResponse> {
    const response = await api.get<ApiResponse<SlaPolicyResponse>>(`${BASE_URL}/${id}`);
    return response.data.data!;
  },

  async create(data: CreateSlaPolicyRequest): Promise<SlaPolicyResponse> {
    const response = await api.post<ApiResponse<SlaPolicyResponse>>(BASE_URL, data);
    return response.data.data!;
  },

  async update(id: string, data: UpdateSlaPolicyRequest): Promise<SlaPolicyResponse> {
    const response = await api.put<ApiResponse<SlaPolicyResponse>>(`${BASE_URL}/${id}`, data);
    return response.data.data!;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },

  /**
   * `data.policyIds` tem que ser o CONJUNTO COMPLETO de políticas ATIVAS do
   * `entityType` em questão, na nova ordem — nunca uma lista parcial.
   *
   * `SlaPoliciesService.reorder` (backend) valida contra `findActiveForCompany`
   * (o mesmo universo que a resolução de SLA percorre) e devolve **400** se
   * faltar ou sobrar algum id: "Faltaram: …" / "Não pertencem ao conjunto: …".
   * Uma lista parcial reposicionaria o conjunto inteiro em silêncio se o
   * servidor aceitasse — por isso ele recusa em vez de completar por conta
   * própria. O chamador (tela de políticas) precisa listar TODAS as políticas
   * do `entityType` (sem paginação parcial) antes de permitir o arrasto.
   */
  async reorder(data: ReorderSlaPoliciesRequest): Promise<SlaPolicyResponse[]> {
    const response = await api.post<ApiResponse<SlaPolicyResponse[]>>(`${BASE_URL}/reorder`, data);
    return response.data.data!;
  },

  async setDefault(id: string): Promise<SlaPolicyResponse> {
    const response = await api.post<ApiResponse<SlaPolicyResponse>>(`${BASE_URL}/${id}/set-default`, {});
    return response.data.data!;
  },
};
